import { CoreButtonView, CoreTextView, TCoreButtonStyleProps, type TCoreButtonViewProps, TCoreTextStyleProps } from '@core/widgets';
import { TViewCommonOption, TWidget } from '@libs/types';
import React, { ElementType, MouseEventHandler } from 'react';
import { TRowActionData } from '../../types/data';
import { TRowActionOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import styles from './styles/rowAction.module.css';

export type TRowActionStyleProps = Pick<TRowActionOption, 'margin' | 'subtitleTextPosition'>;

export type TRowActionViewProps = {
    /**
     * Used for data-test uid and potentially theme resolution.
     * Optional: when omitted, a minimal widget is synthesized internally.
     */
    widget?: TWidget<TRowActionData, TRowActionOption>;
    title?: string;
    subtitle?: string;
    helperText?: string;
    iconName?: string;
    isSubtitleTextPositionTop?: boolean;
    onClick: MouseEventHandler<HTMLButtonElement>;
    as?: ElementType;
    children?: React.ReactNode;
    /** Style overrides for the widget. */
    styleProps?: TRowActionStyleProps;
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'margin' | 'parentStyleNames' | 'styleName'>;

const CONTAINER_STYLE_PROPS: TCoreButtonStyleProps = {
    main: 'var(--color-white)',
    padding: { top: 16, right: 24, bottom: 16, left: 24 },
    gap: 16,
    fillContainer: true,
    cursorDisabled: 'default',
    textAlign: 'start',
    disabled: {
        main: 'var(--color-white)',
    },
    hovered: {
        main: 'var(--color-grey_16_lc)',
    },
    pressed: {
        main: 'var(--color-grey_8_lc)',
    },
};

const TITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    maxLines: 2,
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const SUBTITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: 'var(--color-grey_72_lc)',
    maxLines: 2,
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

/**
 * Composable building block for the RowAction widget.
 * Owns only the rendering logic; accepts pre-resolved behavior props from the parent.
 *
 * `widget` is optional — when omitted, a minimal widget is synthesized,
 * which is sufficient for standalone usages.
 */
export const RowActionView = ({
    widget,
    title,
    subtitle,
    helperText,
    iconName,
    isSubtitleTextPositionTop = true,
    isDisabled,
    isHide,
    onClick,
    margin,
    as = 'button',
    children,
    styleProps,
    parentStyleNames = [],
}: TRowActionViewProps) => {
    const resolvedWidget: TCoreButtonViewProps['widget'] =
        (widget as unknown as TCoreButtonViewProps['widget']) ?? ({ uid: '', data: {}, options: {} } as TCoreButtonViewProps['widget']);

    // In this specific consumer widget, we merge margin from styleProps if provided
    const finalMargin = styleProps?.margin ?? margin;

    return (
        <CoreButtonView
            widget={resolvedWidget}
            text={title ?? subtitle ?? helperText}
            isDisabled={isDisabled}
            isHide={isHide}
            onClick={onClick}
            as={as}
            parentStyleNames={parentStyleNames}
            styleName="rowAction"
            styleProps={{
                ...CONTAINER_STYLE_PROPS,
                margin: finalMargin,
            }}
        >
            <div className={styles.textContent}>
                <div className={styles.topTextContent}>
                    {isSubtitleTextPositionTop && subtitle && (
                        <CoreTextView
                            uid={'subtitle-' + resolvedWidget.uid}
                            text={subtitle}
                            styleProps={SUBTITLE_STYLE_PROPS}
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowActionSubtitle"
                        />
                    )}
                    {title && (
                        <CoreTextView
                            uid={'title-' + resolvedWidget.uid}
                            text={title}
                            styleProps={TITLE_STYLE_PROPS}
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowActionTitle"
                        />
                    )}
                    {!isSubtitleTextPositionTop && subtitle && (
                        <CoreTextView
                            uid={'subtitle-' + resolvedWidget.uid}
                            text={subtitle}
                            styleProps={SUBTITLE_STYLE_PROPS}
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowActionSubtitle"
                        />
                    )}
                </div>
                {isSubtitleTextPositionTop && helperText && (
                    <CoreTextView
                        uid={'helperText-' + resolvedWidget.uid}
                        text={helperText}
                        styleProps={SUBTITLE_STYLE_PROPS}
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowActionHelperText"
                    />
                )}
            </div>
            {iconName && (
                <VisualView
                    uid={`icon-${resolvedWidget.uid}`}
                    iconName={iconName}
                    size={'small'}
                    isDisabled={isDisabled}
                    parentStyleNames={[...parentStyleNames, 'rowActionIcon']}
                />
            )}
            {children}
        </CoreButtonView>
    );
};
